import { useHistory } from "react-router";
import { Info, TriangleAlert } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import {
  PermissionsWrapper,
  PermissionTooltipButton,
} from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { CreditsInfo, Subscription as SubscriptionData } from "data/models";
import * as Routes from "routes";
import { formatTimestamp } from "utils";
import { toTitleCase } from "utils/commons";
import { formatInterval } from "utils/format";
import { Label } from "@/components/ui/label";

interface CurrentPlanCardProps {
  subscription: SubscriptionData;
  credits?: CreditsInfo;
  hasScheduledUpgradeOrDowngrade: boolean;
  hasScheduledCancellation: boolean;
}

export const CurrentPlanCard = ({
  subscription,
  credits,
  hasScheduledUpgradeOrDowngrade,
  hasScheduledCancellation,
}: CurrentPlanCardProps) => {
  const history = useHistory();
  const { isPermitted } = usePermissions();

  const active = subscription.activeSubscription;

  if (!active) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Current Plan: None</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="warning">
            <TriangleAlert />
            <AlertDescription>
              Hub is better with a sub, subscribe to get more out of Hub.
            </AlertDescription>
          </Alert>
        </CardContent>
        <CardFooter className="flex justify-end">
          <PermissionTooltipButton
            permissionKeys={[PermissionsKeys.subscriptionUpdate]}
            additionalDisabled={
              hasScheduledUpgradeOrDowngrade || hasScheduledCancellation
            }
            onClick={() => history.push(Routes.orgManageSubscription)}
          >
            Manage Subscription
          </PermissionTooltipButton>
        </CardFooter>
      </Card>
    );
  }

  if (!credits) return null;

  const creditsPct = Math.min(
    (credits.subCreditsSpent / credits.subCreditsLimit) * 100,
    100,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {toTitleCase(active.plan.product_display_name)}
          {active.trialType ? " (Trial)" : ""}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Term</Label>
          <p className="text-base">{formatInterval(active.plan.cadence)}</p>
        </div>
        <div>
          <Label>Renewal Date</Label>
          <p className="text-base">
            {formatTimestamp(active.renewalDate * 1000)}
          </p>
        </div>
        <div>
          <Label>Credit Usage</Label>
          <div className="flex items-center gap-2">
            <Progress value={creditsPct} className="h-2" />
            <p className="text-muted-foreground text-base whitespace-nowrap">
              {credits.subCreditsSpent.toLocaleString()}/
              {credits.subCreditsLimit.toLocaleString()}
            </p>
          </div>
        </div>
        <div>
          <Label>Support Add-ons</Label>
          <p className="text-base">-</p>
        </div>

        {isPermitted([PermissionsKeys.overageUpdate]) &&
          !credits.isOverageEnabled && (
            <Alert variant="warning">
              <TriangleAlert />
              <AlertDescription>
                When you run out of credit, your apps will stop!
              </AlertDescription>
            </Alert>
          )}

        <PermissionsWrapper
          keys={[PermissionsKeys.subscriptionUpdate]}
          messages={{
            [PermissionsReasons.ROLE_DENY]: <></>,
            [PermissionsReasons.EXTERNALLY_MANAGED]: (
              <Alert variant="info">
                <Info />
                <AlertDescription>
                  To manage your plan please contact support
                </AlertDescription>
              </Alert>
            ),
          }}
        >
          {active.trialType === "user" && (
            <Alert variant="warning">
              <TriangleAlert />
              <AlertDescription>
                Your {active.plan.display_name} trial finishes on{" "}
                {formatTimestamp(active.renewalDate * 1000)}. Your subscription
                will automatically activate and you'll be charged for the{" "}
                {active.plan.display_name} on this date.
              </AlertDescription>
            </Alert>
          )}
          {active.trialType === "admin" && !hasScheduledUpgradeOrDowngrade && (
            <Alert variant="warning">
              <TriangleAlert />
              <AlertDescription>
                Your {active.plan.display_name} trial finishes on{" "}
                {formatTimestamp(active.renewalDate * 1000)}. Your subscription
                will automatically cancel on this date.
              </AlertDescription>
            </Alert>
          )}
        </PermissionsWrapper>
      </CardContent>
      <CardFooter className="flex justify-end">
        <PermissionTooltipButton
          permissionKeys={[PermissionsKeys.subscriptionUpdate]}
          additionalDisabled={
            hasScheduledUpgradeOrDowngrade || hasScheduledCancellation
          }
          onClick={() => history.push(Routes.orgManageSubscription)}
        >
          Manage Subscription
        </PermissionTooltipButton>
      </CardFooter>
    </Card>
  );
};
